import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService, logger } from '@icc/shared';
import { TranslateService } from '@ngx-translate/core';
import { Contractor, ContractorResource } from '../interfaces/contractor.interface';
import { Resource } from '../interfaces/resource.interface';
import { CompanyWorker } from '../interfaces/worker.interface';
import { SalesDocument } from '../models/sales-document.model';

@Injectable({
  providedIn: 'root'
})
export class PersonsService {
    workers: CompanyWorker[] = [];
    workersList: Record<number, string> = {};
    accountManagers: CompanyWorker[] = [];
    accountManagersList: Record<number, string> = {};
    contractors: Contractor[] = [];
    departments: Record<number, { name: string; priority: number }> = {};
    configuratorTypesToDepartments: Record<string, number[]> = {};
    resources: ContractorResource[] = [];
    loaded: Promise<boolean>;
    _loaded = false;
    loading = false;
    private resolve: (value: boolean) => void;

    constructor(private http: HttpClient, private configService: ConfigService, private translateService: TranslateService) {
        this.loaded = new Promise(resolve => {
            this.resolve = resolve;
        })
    }

    async load() {
        if (!this.loading) {
            this.loading = true;
            await Promise.all([
                this.loadWorkers(),
                this.loadContractors(),
                this.loadDepartments(),
            ]);
            this.loadResources();
            this._loaded = true;
            this.resolve(true);
            logger.log('loaded persons');
        }
    }

    async loadWorkers() {
        try {
            const data = await this.http
                .get<CompanyWorker[]>(`${window.location.origin}/iccadmin/trade/user/persons/company-persons-list.json`)
                .toPromise();
            this.workers = data;
            this.setWorkersList();
        } catch (e) {
            console.error(e);
        }
    }

    getWorker(id: number) {
        return this.workers.find(c => c.id === id);
    }

    getWorkerByUserId(userId: number) {
        return this.workers.find(
            (worker) =>
                Number(userId) === Number(worker.person?.user?.id)
        )
    }

    getWorkers() {
        return this.workers;
    }

    getWorkersList() {
        return this.workersList;
    }

    private setWorkersList() {
        this.workersList = this.workers.reduce((acc, cur) => {
            if (cur.person?.user?.id) {
                acc[cur.person?.user?.id] =  cur.person?.reference_name
                    ? cur.person.reference_name
                    : cur.person.first_name+ ' ' + cur.person.last_name;
            }
            return acc;
        }, {});

        this.accountManagers = this.workers.filter(worker => worker.managingContractors);
        this.accountManagersList = this.accountManagers.reduce((acc, cur) => {
            if (cur.person?.user?.id) {
                acc[cur.person?.user?.id] = cur.person?.reference_name
                    ? cur.person.reference_name
                    : cur.person.first_name + ' ' + cur.person.last_name;
            }
            return acc;
        }, {});
    }

    public async loadContractors() {
        try {
            const contractors = await this.http.get<Contractor[]>(
                `${window.location.origin}/iccadmin/trade/user/contractors/index_trade.json`
            ).toPromise();
            this.contractors = contractors;
        } catch (e) {
            console.error(e);
        }
    }

    public async loadContractorsContractors(companyId: number) {
        return await this.http
            .get<Contractor[]>(
                `${window.location.origin}/iccadmin/trade/user/contractors/index_trade/${companyId}.json`
            )
            .toPromise();
    }

    getContractors() {
        return this.contractors;
    }

    public async loadDepartments() {
        try {
            const response = await this.http.get<{
                departments: Record<number, { name: string; priority: number } | string>,
                configuratorTypes: Record<string, number[]>
            }>(
                `${window.location.origin}/api/b2b/departments`
            ).toPromise();

            this.departments = Object.keys(response.departments || {}).reduce((acc, key) => {
                const dept = response.departments[+key];
                if (typeof dept === 'string') {
                    acc[+key] = { name: dept, priority: 0 };
                } else {
                    acc[+key] = dept;
                }
                return acc;
            }, {} as Record<number, { name: string; priority: number }>);

            this.configuratorTypesToDepartments = response.configuratorTypes || {};
        } catch (e) {
            console.error(e);
        }
    }

    getDepartments() {
        return this.departments;
    }

    getConfiguratorTypesToDepartments() {
        return this.configuratorTypesToDepartments;
    }

    loadResources() {
        this.resources = this.configService.user.user.Company.Resources;
        this.resources.forEach(resource => {
            if (resource.id === -1) {
                resource.name = this.translateService.instant('COMPANY|Siedziba');
            }
        })
    }

    getResources() {
        return this.resources;
    }

    getSeller(document: SalesDocument) {
        return Number(this.configService.user.user.ActiveCompany) ===
            Number(document?.sellerCompanyId)
            ? this.workers.find(
                  (worker) =>
                      Number(document?.sellerUserId) === Number(worker.person?.user?.id)
              )
            : this.configService.user.accountManagers[0];
    }
}
